import { colors } from 'theme/colors';
import { withAlpha } from 'theme/withAlpha';

import { EnumOptions } from 'utils/api/modules/entities/entities.types';

// `solid` fills the selected answering control; `background`/`border` tint the read-only chip.
export type ChoiceTone = {
  solid: string;
  background: string;
  border: string;
};

export type OptionColour = keyof typeof colors.ui.optionColour;

const isOptionColour = (colour: string | null | undefined): colour is OptionColour =>
  typeof colour === 'string' && colour in colors.ui.optionColour;

/** The tone an authored option colour renders with; a colourless option stays neutral. Same alphas as `colors.tint`. */
export const optionChoiceTone = (colour: string | null | undefined): ChoiceTone | undefined => {
  if (!isOptionColour(colour)) {
    return undefined;
  }

  const main = colors.ui.optionColour[colour];

  return { solid: main, background: withAlpha(main, 0.12), border: withAlpha(main, 0.4) };
};

/** Key to solid fill for the answering control, covering only the options that carry a colour. */
export const optionSolidTones = (options: EnumOptions | undefined): Record<string, string> =>
  Object.fromEntries(
    (options || []).flatMap((option) => {
      const tone = optionChoiceTone(option.colour);

      return tone ? [[option.key, tone.solid]] : [];
    })
  );

// The server normalises Yes/No/N-A to exactly these keys and colours; N/A stays untoned.
export const YES_NO_NA_TONE: Record<string, ChoiceTone> = {
  yes: optionChoiceTone('success') as ChoiceTone,
  no: optionChoiceTone('error') as ChoiceTone,
};

// The answering control takes a flat map, so project it off the same source.
export const YES_NO_NA_SOLID_TONE: Record<string, string> = {
  yes: YES_NO_NA_TONE.yes.solid,
  no: YES_NO_NA_TONE.no.solid,
};
